import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { BaseResponse } from "../../../shared/types";

import { meApi } from "../../../entities/me/model/me-api";
import { getUserAgent } from "../../../shared/lib/device";
import { localStorageService } from "../../../shared/api";
import {
  EmailVerificationRequest,
  SmsAuthRequest,
  AuthResponse,
} from "./types";

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_BASE_URL}/auth`,
  }),
  endpoints: (builder) => ({
    guestAuth: builder.mutation<AuthResponse, void>({
      query: () => ({
        url: "/guest",
        method: "POST",
        body: {
          userAgent: getUserAgent(),
          fingerprint: getUserAgent(),
        },
      }),
      transformResponse(response: AuthResponse) {
        localStorageService.setToken(response.token);
        localStorageService.setRefreshToken(response.refreshToken);
        return response;
      },
    }),

    sendSmsCode: builder.mutation<
      BaseResponse,
      { phoneNumber: string; token: string }
    >({
      query: (body) => ({
        url: "/login/sms/send",
        method: "POST",
        body,
        headers: {
          authorization: `Bearer ${body.token}`,
        },
      }),
    }),

    smsAuth: builder.mutation<AuthResponse, SmsAuthRequest>({
      query: (credentials) => {
        return {
          url: "/login/sms",
          method: "POST",
          body: credentials,
          headers: {
            authorization: `Bearer ${credentials.token}`,
          },
        };
      },
      transformResponse(response: AuthResponse) {
        if (response.token) localStorageService.setToken(response.token);
        if (response.refreshToken)
          localStorageService.setRefreshToken(response.refreshToken);

        return response;
      },
    }),

    refreshToken: builder.mutation<AuthResponse, string>({
      query: (refreshToken) => {
        return {
          url: "/refresh",
          method: "POST",
          headers: {
            authorization: `Bearer ${refreshToken}`,
          },
        };
      },
      async transformResponse(response: AuthResponse) {
        if (response.token) localStorageService.setToken(response.token);
        if (response.refreshToken)
          localStorageService.setRefreshToken(response.refreshToken);
        return response;
      },
    }),

    logout: builder.mutation<BaseResponse, string>({
      query: (token) => ({
        url: "/logout",
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          localStorageService.clearToken();
          localStorageService.clearRefreshToken();
          await dispatch(authApi.endpoints.guestAuth.initiate());
          window.location.reload();
        } catch (error) {
          console.error("Logout error:", error);
        }
      },
    }),

    blockAccount: builder.mutation<BaseResponse, string>({
      query: (token) => ({
        url: "/block",
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
      }),
    }),

    sendEmailVerification: builder.mutation<
      BaseResponse,
      { email: string; token: string }
    >({
      query: ({ token, ...body }) => ({
        url: "/verify/email/send",
        method: "POST",
        body,
        headers: {
          authorization: `Bearer ${token}`,
        },
      }),
    }),

    verifyEmail: builder.mutation<BaseResponse, EmailVerificationRequest>({
      query: ({ token, ...body }) => ({
        url: "/verify/email",
        method: "POST",
        body,
        headers: {
          authorization: `Bearer ${token}`,
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled;

          if (result.data.result) {
            await dispatch(
              meApi.endpoints.getMyProfile.initiate(undefined, {
                forceRefetch: true,
              })
            );
          }
        } catch (error) {
          console.error("Error during email verification:", error);
        }
      },
    }),
  }),
});

export const {
  useGuestAuthMutation,
  useSendSmsCodeMutation,
  useSmsAuthMutation,
  useRefreshTokenMutation,
  useLogoutMutation,
  useBlockAccountMutation,
  useSendEmailVerificationMutation,
  useVerifyEmailMutation,
} = authApi;
